import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useCallback, useRef, useState } from 'react'

import {
  postCollectionRepayment,
  postCollectionSavingsDeposit,
  postCollectionSecurityDepositTopUp,
} from '@/api/collections-api'
import type { CollectionRepaymentAllocation } from '@/api/collections-types'
import { groupKeys } from '@/features/groups/group-keys'
import { ApiError } from '@/lib/problem-details'
import type { Pesewas } from '@/lib/money'

import { COLLECTION_CASH_SOURCE, type CollectionColumn } from './constants'
import {
  journalKey,
  newOperationId,
  readJournal,
  writeJournal,
  type SheetJournal,
} from './sheet-journal'
import { readAmount, type SheetAmounts, type SheetRow } from './sheet-model'

/**
 * Posting a sheet.
 *
 * **A batch is not a transaction here, and the screen never pretends otherwise.** The contract
 * exposes no endpoint that takes a sheet: a collection is posted one line at a time through the
 * module that owns the money — `/loan-cycles/{id}/repayments`, `/savings/accounts/{id}/deposits`,
 * `/savings/accounts/{id}/security-deposit`. So a batch of forty lines is forty requests, and a
 * refusal on line twelve leaves eleven posted. Any design that hid that would be lying about where
 * a group's money is.
 *
 * What can honestly be built on top of that is:
 *
 * - **Order.** Lines post in sheet order, one at a time, never in parallel. Forty concurrent
 *   requests against one branch's open day is not a batch, it is a stampede, and the order the
 *   ledger receives them in should be the order they were written on the paper.
 * - **Continuation.** A refusal does not stop the run. The remaining members are not made to wait
 *   because one round was closed this morning; the refusal is recorded against its line and the
 *   sheet carries on.
 * - **Idempotence.** Every line carries an operation id generated once and reused on every retry,
 *   so re-running a batch after a failure re-posts nothing that already landed. The server answers
 *   a replay with the original posting and says `wasReplay`, which the screen reports as "already
 *   posted" rather than as a second success.
 */

export type PreparedLineStatus = 'queued' | 'posting' | 'posted' | 'refused'

export interface PreparedLine {
  /** Stable for the life of the sheet: `${customerId}:${column}`. */
  key: string
  lineNumber: number
  column: CollectionColumn
  customerId: string
  memberName: string
  customerNumber: string
  targetId: string
  targetReference: string
  amountMinor: Pesewas
  currencyCode: string
  clientOperationId: string
  narration: string
}

export interface PostedLine {
  status: 'posted'
  /** What the server says it took, which is not always what was keyed — see `wasReplay`. */
  postedAmountMinor: Pesewas
  reference: string
  /** True when this matched an earlier posting of the same operation id and changed nothing. */
  wasReplay: boolean
  /** The balance the account or round now carries. */
  balanceAfterMinor: Pesewas
  /** Set on a repayment: how the server split the money. */
  allocation: CollectionRepaymentAllocation | null
  /** Set on a repayment that settled the round in full. */
  cycleClosed: boolean
}

export interface RefusedLine {
  status: 'refused'
  error: ApiError
}

export type LineResult =
  | { status: 'queued' }
  | { status: 'posting' }
  | PostedLine
  | RefusedLine

export interface BatchSummary {
  postedCount: number
  replayedCount: number
  refusedCount: number
  postedMinor: Pesewas
}

export interface BatchContext {
  groupId: string
  groupCode: string
  meetingId: string
  meetingDate: string
  valueDate: string
}

/**
 * Turns the keyed amounts into the lines that will be sent, in sheet order.
 *
 * Operation ids come from the sheet's journal, so a line prepared a second time — a retry, or the
 * same sheet reopened after a reload — carries the id it carried the first time.
 *
 * Lines the journal already records as settled are left out entirely. Re-sending one would be
 * harmless — the server replays it — but it would put a line in the operator's review list that
 * nothing is going to happen to, and a review list has to be exactly what is about to be sent.
 */
export function prepareLines(
  rows: SheetRow[],
  amounts: SheetAmounts,
  context: BatchContext,
  journal: SheetJournal,
): { lines: PreparedLine[]; journal: SheetJournal } {
  const operationIds = { ...journal.operationIds }
  const lines: PreparedLine[] = []

  for (const row of rows) {
    for (const column of Object.keys(row.cells) as CollectionColumn[]) {
      const cell = row.cells[column]
      const amount = readAmount(amounts, row.customerId, column)

      if (amount === null || amount <= 0 || cell.target === null) {
        continue
      }

      if (cell.availability.kind !== 'available') {
        continue
      }

      const key = journalKey(row.customerId, column)
      if (key in journal.posted) {
        continue
      }

      operationIds[key] ??= newOperationId()

      lines.push({
        key,
        lineNumber: row.lineNumber,
        column,
        customerId: row.customerId,
        memberName: row.fullName,
        customerNumber: row.customerNumber,
        targetId: cell.target.id,
        targetReference: cell.target.reference,
        amountMinor: amount,
        currencyCode: cell.target.currencyCode,
        clientOperationId: operationIds[key],
        // The only trail from a ledger entry back to a line of paper somebody is holding. The
        // repayment endpoint also carries the meeting id as a field; the savings endpoints do not,
        // so for those this narration is the whole of the trail.
        narration: `Collection sheet line ${String(row.lineNumber)} — ${context.groupCode} meeting ${context.meetingDate}`,
      })
    }
  }

  return { lines, journal: { operationIds, posted: journal.posted } }
}

async function postOne(line: PreparedLine, context: BatchContext): Promise<PostedLine> {
  if (line.column === 'LoanRepayment') {
    const posted = await postCollectionRepayment(line.targetId, {
      amountMinor: line.amountMinor,
      valueDate: context.valueDate,
      channel: COLLECTION_CASH_SOURCE,
      // No endpoint on this contract exposes the numeric till identifier the repayment takes, so
      // the posting falls to the channel's own cash account. See the area's gaps note.
      tillId: null,
      clientOperationId: line.clientOperationId,
      groupMeetingId: context.meetingId,
      narration: line.narration,
    })

    return {
      status: 'posted',
      postedAmountMinor: posted.amountMinor,
      reference: posted.journalEntry ?? posted.transactionId,
      // The repayment endpoint replays silently rather than reporting it, so a replayed line is
      // not distinguishable here. It is still safe — the money posts once — and the amount that
      // comes back is the amount that actually landed, which is what the reconciliation compares.
      wasReplay: false,
      balanceAfterMinor: posted.cycle.totalOutstandingMinor,
      allocation: posted.allocation,
      cycleClosed: posted.cycleClosed,
    }
  }

  const request = {
    amountMinor: line.amountMinor,
    currencyCode: line.currencyCode,
    valueDate: context.valueDate,
    narration: line.narration,
    clientOperationId: line.clientOperationId,
  }

  const posting =
    line.column === 'SavingsDeposit'
      ? await postCollectionSavingsDeposit(line.targetId, request)
      : await postCollectionSecurityDepositTopUp(line.targetId, request)

  return {
    status: 'posted',
    postedAmountMinor: posting.transaction.amountMinor,
    reference: posting.transaction.transactionNumber,
    wasReplay: posting.wasReplay,
    balanceAfterMinor: posting.balanceAfterMinor,
    allocation: null,
    cycleClosed: false,
  }
}

export interface BatchPoster {
  results: Record<string, LineResult>
  /** The line currently in flight, so the grid can mark it. */
  activeKey: string | null
  isPosting: boolean
  summary: BatchSummary | null
  post: (lines: PreparedLine[], context: BatchContext) => Promise<BatchSummary>
  /** Forget a finished run so the sheet can be posted again after it is re-keyed. */
  reset: () => void
}

export function useBatchPoster(meetingId: string): BatchPoster {
  const queryClient = useQueryClient()
  const [results, setResults] = useState<Record<string, LineResult>>({})
  const [activeKey, setActiveKey] = useState<string | null>(null)
  const [summary, setSummary] = useState<BatchSummary | null>(null)
  const meetingIdRef = useRef(meetingId)
  meetingIdRef.current = meetingId

  /**
   * A mirror of `results` the run can read synchronously.
   *
   * The second run of a sheet posts only the lines that were refused, and it must not wipe the
   * markers on the lines that already succeeded — the operator is looking at both in the same
   * list. So outcomes accumulate here across runs and the state is replaced from this, rather than
   * each run starting from an empty map.
   */
  const resultsRef = useRef<Record<string, LineResult>>({})

  const applyResult = (key: string, result: LineResult) => {
    resultsRef.current = { ...resultsRef.current, [key]: result }
    setResults(resultsRef.current)
  }

  const mutation = useMutation({
    mutationFn: async ({ lines, context }: { lines: PreparedLine[]; context: BatchContext }) => {
      for (const line of lines) {
        applyResult(line.key, { status: 'queued' })
      }

      for (const line of lines) {
        setActiveKey(line.key)
        applyResult(line.key, { status: 'posting' })

        try {
          const posted = await postOne(line, context)
          applyResult(line.key, posted)

          // Written down the moment it settles rather than at the end of the run: a batch
          // interrupted by a closed laptop must leave behind what it already did.
          const journal = readJournal(meetingIdRef.current)
          journal.posted[line.key] = {
            amountMinor: posted.postedAmountMinor,
            reference: posted.reference,
            postedAtIso: new Date().toISOString(),
          }
          journal.operationIds[line.key] = line.clientOperationId
          writeJournal(meetingIdRef.current, journal)
        } catch (error) {
          applyResult(line.key, {
            status: 'refused',
            error:
              error instanceof ApiError
                ? error
                : new ApiError({
                    status: 0,
                    code: 'collections.line_failed',
                    title: 'This line could not be posted',
                    detail: error instanceof Error ? error.message : undefined,
                  }),
          })
        }
      }

      setActiveKey(null)

      // Tallied over everything this sheet has done, not just this run, because that is the
      // question being asked: what posted, and what still has not.
      const tally: BatchSummary = {
        postedCount: 0,
        replayedCount: 0,
        refusedCount: 0,
        postedMinor: 0,
      }

      for (const result of Object.values(resultsRef.current)) {
        if (result.status === 'posted') {
          if (result.wasReplay) {
            tally.replayedCount += 1
          } else {
            tally.postedCount += 1
          }
          tally.postedMinor += result.postedAmountMinor
        } else if (result.status === 'refused') {
          tally.refusedCount += 1
        }
      }

      setSummary(tally)
      return tally
    },
    onSettled: () => {
      // Balances have moved, and the ceiling on the next repayment is one of them. The group is
      // invalidated too: a collection is an input to the on-time repayment rate that the next
      // round's eligibility turns on.
      void queryClient.invalidateQueries({ queryKey: ['collections'] })
      void queryClient.invalidateQueries({ queryKey: groupKeys.all })
    },
  })

  const post = useCallback(
    (lines: PreparedLine[], context: BatchContext) => mutation.mutateAsync({ lines, context }),
    [mutation],
  )

  const reset = useCallback(() => {
    resultsRef.current = {}
    setResults({})
    setSummary(null)
    setActiveKey(null)
    mutation.reset()
  }, [mutation])

  return {
    results,
    activeKey,
    isPosting: mutation.isPending,
    summary,
    post,
    reset,
  }
}
